import React, { useEffect, useMemo, useState, useRef } from 'react';
import { StoreIcon, CalendarIcon, DollarSignIcon, TagIcon, PlusIcon, SettingsIcon, ArrowRightIcon, SendIcon, ClockIcon, CheckCircleIcon, XCircleIcon, InfoIcon, FileTextIcon, AlertTriangleIcon, ListIcon, CalendarDaysIcon } from 'lucide-react';
import { User, Store, BrandRequest, Province, StoreTier } from '../data/types';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Tooltip } from '../components/ui/Tooltip';
import { AddStoreModal } from '../components/admin/AddStoreModal';
import { PageTransition } from '../components/ui/PageTransition';
import { Pagination } from '../components/ui/Pagination';
import { Skeleton } from '../components/ui/Skeleton';
import { BookingOpportunityModal } from '../components/grapevine/BookingOpportunityModal';
import { DashboardCalendar } from '../components/dashboard/DashboardCalendar';
interface GrapevineDashboardPageProps {
  user: User;
  stores: Store[];
  brandRequests: BrandRequest[];
  onLogout: () => void;
  onNavigate: (view: any) => void;
  onAddStore: (store: {
    name: string;
    location: string;
    province: Province;
    tier: StoreTier;
  }) => void;
  onStoreSelect: (store: Store) => void;
  onSendOpportunity: (request: any, sendNow?: boolean) => void;
  activityLog?: any;
  brands?: any[];
  defaultToCalendar?: boolean;
}
export function GrapevineDashboardPage({
  user,
  stores,
  brandRequests,
  onLogout,
  onNavigate,
  onAddStore,
  onStoreSelect,
  onSendOpportunity,
  activityLog,
  brands,
  defaultToCalendar
}: GrapevineDashboardPageProps) {
  const [isAddStoreModalOpen, setIsAddStoreModalOpen] = useState(false);
  const [isOpportunityModalOpen, setIsOpportunityModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'calendar'>(defaultToCalendar ? 'calendar' : 'overview');
  const storeNetworkRef = useRef<HTMLDivElement>(null);
  const pageSize = 10;
  // Initial load simulation
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);
  // Switch to calendar tab when defaultToCalendar changes
  useEffect(() => {
    if (defaultToCalendar) {
      setActiveTab('calendar');
    }
  }, [defaultToCalendar]);
  // Calculate platform stats
  const stats = useMemo(() => {
    let activeBookings = 0;
    let totalRevenue = 0;
    let totalSlots = 0;
    const activeBrands = new Map<string, number>();
    const tierCounts = {
      flagship: 0,
      'high-traffic': 0,
      standard: 0
    };
    const revenueByTier = {
      flagship: 0,
      'high-traffic': 0,
      standard: 0
    };
    stores.forEach(store => {
      tierCounts[store.tier]++;
      store.slots.forEach(slot => {
        totalSlots++;
        if (slot.status === 'booked') {
          activeBookings++;
          totalRevenue += slot.price;
          revenueByTier[store.tier] += slot.price;
          if (slot.brandName) {
            activeBrands.set(slot.brandName, (activeBrands.get(slot.brandName) || 0) + 1);
          }
        }
      });
    });
    const topBrands = Array.from(activeBrands.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
    const utilisation = totalSlots > 0 ? Math.round(activeBookings / totalSlots * 100) : 0;
    return {
      totalStores: stores.length,
      activeBookings,
      totalRevenue,
      activeBrandsCount: activeBrands.size,
      tierCounts,
      revenueByTier,
      topBrands,
      utilisation,
      totalSlots
    };
  }, [stores]);
  // Sent opportunities from brandRequests
  const sentOpportunities = useMemo(() => {
    return brandRequests.filter(r => r.requestedBy === 'woolworths' && r.status !== 'draft').sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  }, [brandRequests]);
  // Brand-initiated requests (incoming from brands)
  const brandInitiatedRequests = useMemo(() => {
    return brandRequests.filter(r => r.requestedBy === 'brand').sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [brandRequests]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const totalPages = Math.ceil(stores.length / pageSize);
  const paginatedStores = stores.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  // Calculate platform health alerts
  const healthAlerts = useMemo(() => {
    const alerts: {
      title: string;
      description: string;
      type: 'warning' | 'info';
    }[] = [];
    if (stats.utilisation < 40) {
      alerts.push({
        title: 'Low Platform Utilisation',
        description: `Overall utilisation is currently at ${stats.utilisation}%. Consider sending out more media quotes to drive bookings.`,
        type: 'warning'
      });
    }
    const unrespondedRequests = brandInitiatedRequests.filter(r => r.status === 'pending').length;
    if (unrespondedRequests > 0) {
      alerts.push({
        title: 'Pending Brand Requests',
        description: `There are ${unrespondedRequests} brand requests awaiting Woolworths approval. Follow up with the admin team to ensure timely responses.`,
        type: 'info'
      });
    }
    return alerts;
  }, [stats.utilisation, brandInitiatedRequests]);
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-1755">
      <Header user={user} onLogout={onLogout} currentView="grapevine-dashboard" onNavigate={onNavigate} stores={stores} brands={brands} brandRequests={brandRequests} activityEntries={activityLog?.entries} unreadCount={activityLog?.unreadCount} onMarkAllRead={activityLog?.markAllAsRead} onMarkRead={activityLog?.markAsRead} onSelectStore={onStoreSelect} data-id="element-1756" />

      <PageTransition className="flex-1 flex flex-col" data-id="element-1757">
        {/* Welcome Header */}
        <div className="bg-surface-warm border-b border-border px-6 py-8" data-id="element-1758">
          <div className="max-w-7xl mx-auto" data-id="element-1759">
            <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-1760">
              Grapevine Platform Overview
            </h1>
            <p className="text-text-secondary" data-id="element-1761">
              Manage the Woolworths Media Planner platform, stores, and rate
              cards.
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 py-3 border-b border-border bg-surface-warm flex items-center gap-2" data-id="element-1762">
          <button onClick={() => setActiveTab('overview')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'overview' ? 'bg-white text-primary shadow-sm border border-border' : 'text-text-secondary hover:text-text-primary hover:bg-surface'}`} data-id="element-1763">
            <ListIcon className="w-4 h-4" data-id="element-1764" />
            Overview
          </button>
          <button onClick={() => setActiveTab('calendar')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'calendar' ? 'bg-white text-primary shadow-sm border border-border' : 'text-text-secondary hover:text-text-primary hover:bg-surface'}`} data-id="element-1765">
            <CalendarDaysIcon className="w-4 h-4" data-id="element-1766" />
            Calendar View
          </button>
        </div>

        {activeTab === 'calendar' ? <main className="flex-1 flex flex-col overflow-hidden" data-id="element-1767">
            <DashboardCalendar stores={stores} data-id="element-1768" />
          </main> : <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8 space-y-8" data-id="element-1769">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1770">
              {/* Total Stores */}
              <button type="button" onClick={() => storeNetworkRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })} className="bg-white border border-border rounded-lg p-6 shadow-sm text-left hover:border-text-tertiary hover:shadow-md transition-all cursor-pointer" data-id="element-1771">
                <div className="flex items-start gap-4 mb-4" data-id="element-1772">
                  <div className="w-12 h-12 rounded-full bg-surface-warm flex items-center justify-center flex-shrink-0" data-id="element-1773">
                    <StoreIcon className="w-6 h-6 text-text-primary" data-id="element-1774" />
                  </div>
                  <div data-id="element-1775">
                    <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1776">
                      Total Stores
                    </p>
                    <p className="text-3xl font-bold text-text-primary" data-id="element-1777">
                      {stats.totalStores}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs" data-id="element-1778">
                  <span className="flex items-center gap-1.5" data-id="element-1779">
                    <span className="w-2 h-2 rounded-full bg-primary" data-id="element-1780" />
                    {stats.tierCounts.flagship} flagship
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-1781">
                    <span className="w-2 h-2 rounded-full bg-blocked" data-id="element-1782" />
                    {stats.tierCounts['high-traffic']} high-traffic
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-1783">
                    <span className="w-2 h-2 rounded-full bg-text-tertiary" data-id="element-1784" />
                    {stats.tierCounts.standard} standard
                  </span>
                </div>
              </button>

              {/* Active Bookings + Utilisation */}
              <div className="bg-white border border-border rounded-lg p-6 shadow-sm" data-id="element-1785">
                <div className="flex items-start gap-4 mb-4" data-id="element-1786">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-1787">
                    <CalendarIcon className="w-6 h-6 text-blue-600" data-id="element-1788" />
                  </div>
                  <div data-id="element-1789">
                    <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1790">
                      Active Bookings
                    </p>
                    <p className="text-3xl font-bold text-text-primary" data-id="element-1791">
                      {stats.activeBookings}
                    </p>
                  </div>
                </div>
                <div data-id="element-1792">
                  <div className="flex items-center justify-between text-xs text-text-secondary mb-1.5" data-id="element-1793">
                    <span data-id="element-1794">Platform utilisation</span>
                    <span className="font-semibold text-text-primary" data-id="element-1795">
                      {stats.utilisation}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-surface-warm rounded-full overflow-hidden" data-id="element-1796">
                    <div className={`h-full rounded-full transition-all ${stats.utilisation > 75 ? 'bg-green-500' : stats.utilisation > 40 ? 'bg-blue-500' : 'bg-text-tertiary'}`} style={{
                  width: `${stats.utilisation}%`
                }} data-id="element-1797" />
                  </div>
                </div>
              </div>

              {/* Total Revenue */}
              <div className="bg-white border border-border rounded-lg p-6 shadow-sm" data-id="element-1798">
                <div className="flex items-start gap-4 mb-4" data-id="element-1799">
                  <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-1800">
                    <DollarSignIcon className="w-6 h-6 text-green-600" data-id="element-1801" />
                  </div>
                  <div data-id="element-1802">
                    <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1803">
                      Total Revenue
                    </p>
                    <p className="text-2xl font-bold text-text-primary" data-id="element-1804">
                      {formatCurrency(stats.totalRevenue)}
                    </p>
                  </div>
                </div>
                <div className="space-y-1.5" data-id="element-1805">
                  {(['flagship', 'high-traffic', 'standard'] as const).map(tier => {
                const pct = stats.totalRevenue > 0 ? Math.round(stats.revenueByTier[tier] / stats.totalRevenue * 100) : 0;
                return <div key={tier} className="flex items-center gap-2 text-xs" data-id="element-1806">
                          <span className="w-16 text-text-secondary capitalize" data-id="element-1807">
                            {tier.replace('-', ' ')}
                          </span>
                          <div className="flex-1 h-1.5 bg-surface-warm rounded-full overflow-hidden" data-id="element-1808">
                            <div className={`h-full rounded-full ${tier === 'flagship' ? 'bg-primary' : tier === 'high-traffic' ? 'bg-blocked' : 'bg-text-tertiary'}`} style={{
                      width: `${pct}%`
                    }} data-id="element-1809" />
                          </div>
                          <span className="w-8 text-right font-medium text-text-primary" data-id="element-1810">
                            {pct}%
                          </span>
                        </div>;
              })}
                </div>
              </div>

              {/* Active Brands */}
              <div className="bg-white border border-border rounded-lg p-6 shadow-sm" data-id="element-1811">
                <div className="flex items-start gap-4 mb-4" data-id="element-1812">
                  <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0" data-id="element-1813">
                    <TagIcon className="w-6 h-6 text-orange-600" data-id="element-1814" />
                  </div>
                  <div data-id="element-1815">
                    <p className="text-sm font-medium text-text-secondary mb-1" data-id="element-1816">
                      Active Brands
                    </p>
                    <p className="text-3xl font-bold text-text-primary" data-id="element-1817">
                      {stats.activeBrandsCount}
                    </p>
                  </div>
                </div>
                {stats.topBrands.length > 0 && <div className="space-y-1.5" data-id="element-1818">
                    <p className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wider" data-id="element-1819">
                      Top brands
                    </p>
                    {stats.topBrands.map(([name, count], i) => <div key={name} className="flex items-center justify-between text-xs" data-id="element-1820">
                        <span className="text-text-secondary truncate mr-2" data-id="element-1821">
                          <span className="text-text-tertiary mr-1" data-id="element-1822">
                            {i + 1}.
                          </span>{' '}
                          {name}
                        </span>
                        <span className="font-medium text-text-primary flex-shrink-0" data-id="element-1823">
                          {count} slots
                        </span>
                      </div>)}
                  </div>}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1824">
              {/* Store Network */}
              <div ref={storeNetworkRef} className="lg:col-span-2 space-y-4" data-id="element-1825">
                <div className="flex items-center justify-between" data-id="element-1826">
                  <h2 className="text-xl font-bold text-text-primary flex items-center gap-2" data-id="element-1827">
                    <StoreIcon className="w-5 h-5 text-text-primary" data-id="element-1828" />
                    Store Network
                  </h2>
                  <Button variant="primary" size="sm" onClick={() => setIsAddStoreModalOpen(true)} className="flex items-center gap-1.5 bg-primary hover:bg-primary/90" data-id="element-1829">
                    <PlusIcon className="w-4 h-4" data-id="element-1830" />
                    Add Store
                  </Button>
                </div>

                <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden flex flex-col" data-id="element-1831">
                  <div className="overflow-x-auto" data-id="element-1832">
                    <table className="w-full text-left border-collapse min-w-[600px]" data-id="element-1833">
                      <thead className="bg-surface border-b border-border" data-id="element-1834">
                        <tr data-id="element-1835">
                          <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1836">
                            Store
                          </th>
                          <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1837">
                            Tier
                          </th>
                          <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1838">
                            Slots
                          </th>
                          <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1839">
                            Booked
                          </th>
                          <th className="px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1840">
                            <Tooltip content="Percentage of total slots currently booked" position="left" data-id="element-1841">
                              <span className="cursor-help border-b border-dotted border-text-tertiary" data-id="element-1842">
                                Utilisation
                              </span>
                            </Tooltip>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border" data-id="element-1843">
                        {isLoading ? Array.from({
                      length: Math.min(pageSize, stores.length || 5)
                    }).map((_, i) => <tr key={i} className="bg-white" data-id="element-1844">
                                <td className="px-6 py-4" data-id="element-1845">
                                  <Skeleton variant="text" className="w-32 mb-2" data-id="element-1846" />
                                  <Skeleton variant="text" className="w-24 h-3" data-id="element-1847" />
                                </td>
                                <td className="px-6 py-4" data-id="element-1848">
                                  <Skeleton variant="rect" className="w-16 h-5 rounded-full" data-id="element-1849" />
                                </td>
                                <td className="px-6 py-4 text-right" data-id="element-1850">
                                  <Skeleton variant="text" className="w-6 ml-auto" data-id="element-1851" />
                                </td>
                                <td className="px-6 py-4 text-right" data-id="element-1852">
                                  <Skeleton variant="text" className="w-6 ml-auto" data-id="element-1853" />
                                </td>
                                <td className="px-6 py-4 text-right" data-id="element-1854">
                                  <div className="flex items-center justify-end gap-2" data-id="element-1855">
                                    <Skeleton variant="text" className="w-8" data-id="element-1856" />
                                    <Skeleton variant="rect" className="w-16 h-1.5 rounded-full" data-id="element-1857" />
                                  </div>
                                </td>
                              </tr>) : paginatedStores.map((store, idx) => {
                      const totalSlots = store.slots.length;
                      const bookedSlots = store.slots.filter(s => s.status === 'booked').length;
                      const utilization = totalSlots > 0 ? Math.round(bookedSlots / totalSlots * 100) : 0;
                      return <tr key={store.id} className={`hover:bg-surface/40 transition-colors ${idx % 2 === 1 ? 'bg-surface-warm/30' : ''}`} data-id="element-1858">
                                  <td className="px-6 py-4" data-id="element-1859">
                                    <div className="text-sm font-medium text-text-primary cursor-pointer hover:text-primary transition-colors" onClick={() => onStoreSelect(store)} data-id="element-1860">
                                      {store.name}
                                    </div>
                                    <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1861">
                                      {store.location}, {store.province}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4" data-id="element-1862">
                                    <Badge variant={store.tier} data-id="element-1863">
                                      {store.tier.replace('-', ' ')}
                                    </Badge>
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-text-secondary" data-id="element-1864">
                                    {totalSlots}
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-text-secondary" data-id="element-1865">
                                    {bookedSlots}
                                  </td>
                                  <td className="px-6 py-4 text-right" data-id="element-1866">
                                    <div className="flex items-center justify-end gap-2" data-id="element-1867">
                                      <span className="text-sm font-medium text-text-primary" data-id="element-1868">
                                        {utilization}%
                                      </span>
                                      <div className="w-16 h-1.5 bg-surface-warm rounded-full overflow-hidden" data-id="element-1869">
                                        <div className={`h-full rounded-full ${utilization > 75 ? 'bg-green-500' : utilization > 40 ? 'bg-blue-500' : 'bg-text-tertiary'}`} style={{
                                width: `${utilization}%`
                              }} data-id="element-1870" />
                                      </div>
                                    </div>
                                  </td>
                                </tr>;
                    })}
                      </tbody>
                    </table>
                  </div>
                  {stores.length > pageSize && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-1871" />}
                </div>
              </div>

              {/* Quick Actions + Sent History + Platform Health */}
              <div className="space-y-4" data-id="element-1872">
                <h2 className="text-xl font-bold text-text-primary flex items-center gap-2" data-id="element-1873">
                  <SettingsIcon className="w-5 h-5 text-text-primary" data-id="element-1874" />
                  Platform Settings
                </h2>

                <div className="bg-white border border-border rounded-lg p-6 shadow-sm hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group" onClick={() => setIsOpportunityModalOpen(true)} data-id="element-1875">
                  <div className="flex items-center justify-between mb-4" data-id="element-1876">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors" data-id="element-1877">
                      <SendIcon className="w-5 h-5 text-blue-600" data-id="element-1878" />
                    </div>
                    <ArrowRightIcon className="w-5 h-5 text-text-tertiary group-hover:text-blue-600 transition-colors" data-id="element-1879" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1" data-id="element-1880">
                    Send Media Quote
                  </h3>
                  <p className="text-sm text-text-secondary" data-id="element-1881">
                    Build a curated quote of available media spaces and send it
                    to a brand.
                  </p>
                  <Tooltip content="Select available inventory slots, preview as a professional quote, and send directly to a brand" position="bottom" data-id="element-1882">
                    <span className="inline-flex items-center gap-1 text-xs text-primary mt-2 cursor-help" data-id="element-1883">
                      <InfoIcon className="w-3 h-3" data-id="element-1884" /> How does this work?
                    </span>
                  </Tooltip>
                </div>

                <div className="bg-white border border-border rounded-lg p-6 shadow-sm hover:border-text-tertiary hover:shadow-md transition-all cursor-pointer group" onClick={() => onNavigate('rate-cards')} data-id="element-1885">
                  <div className="flex items-center justify-between mb-4" data-id="element-1886">
                    <div className="w-10 h-10 rounded-full bg-surface-warm flex items-center justify-center group-hover:bg-surface transition-colors" data-id="element-1887">
                      <DollarSignIcon className="w-5 h-5 text-text-primary" data-id="element-1888" />
                    </div>
                    <ArrowRightIcon className="w-5 h-5 text-text-tertiary group-hover:text-text-primary transition-colors" data-id="element-1889" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1" data-id="element-1890">
                    Manage Rate Cards
                  </h3>
                  <p className="text-sm text-text-secondary" data-id="element-1891">
                    Set base pricing for all media types and store tiers. Adjust
                    seasonal multipliers.
                  </p>
                </div>

                {/* Platform Health Alerts */}
                {healthAlerts.length > 0 && <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-1892">
                    <div className="px-5 py-3 border-b border-border bg-orange-50 flex items-center gap-2" data-id="element-1893">
                      <AlertTriangleIcon className="w-4 h-4 text-orange-600" data-id="element-1894" />
                      <h3 className="text-sm font-semibold text-text-primary" data-id="element-1895">
                        Platform Health Alerts
                      </h3>
                    </div>
                    <div className="divide-y divide-border" data-id="element-1896">
                      {healthAlerts.map((alert, idx) => <div key={idx} className="px-5 py-4" data-id="element-1897">
                          <h4 className={`text-sm font-semibold mb-1 ${alert.type === 'warning' ? 'text-orange-700' : 'text-blue-700'}`} data-id="element-1898">
                            {alert.title}
                          </h4>
                          <p className="text-xs text-text-secondary" data-id="element-1899">
                            {alert.description}
                          </p>
                        </div>)}
                    </div>
                  </div>}

                {/* Incoming Brand Requests */}
                <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-1900">
                  <div className="px-5 py-3 border-b border-border bg-amber-50 flex items-center justify-between" data-id="element-1901">
                    <div className="flex items-center gap-2" data-id="element-1902">
                      <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-1903" />
                      <h3 className="text-sm font-semibold text-text-primary" data-id="element-1904">
                        Incoming Brand Requests
                      </h3>
                    </div>
                    {brandInitiatedRequests.length > 0 && <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[11px] font-bold bg-amber-500 text-white rounded-full" data-id="element-1905">
                        {brandInitiatedRequests.filter(r => r.status === 'pending').length}
                      </span>}
                  </div>
                  {brandInitiatedRequests.length === 0 ? <div className="px-5 py-8 text-center" data-id="element-1906">
                      <ClockIcon className="w-8 h-8 text-text-tertiary mx-auto mb-2 opacity-40" data-id="element-1907" />
                      <p className="text-sm text-text-secondary" data-id="element-1908">
                        No brand requests yet
                      </p>
                      <p className="text-xs text-text-tertiary mt-1" data-id="element-1909">
                        When brands request media space, they'll appear here
                      </p>
                    </div> : <div className="divide-y divide-border" data-id="element-1910">
                      {brandInitiatedRequests.map(req => <div key={req.id} className="px-5 py-3 flex items-center justify-between" data-id="element-1911">
                          <div className="min-w-0" data-id="element-1912">
                            <div className="text-sm font-medium text-text-primary truncate" data-id="element-1913">
                              {req.brandName}
                            </div>
                            <div className="text-xs text-text-tertiary mt-0.5 flex items-center gap-2" data-id="element-1914">
                              <span data-id="element-1915">{req.campaignName || 'Unnamed'}</span>
                              <span data-id="element-1916">·</span>
                              <span data-id="element-1917">
                                {req.stores.length} store
                                {req.stores.length !== 1 ? 's' : ''}
                              </span>
                              <span data-id="element-1918">·</span>
                              <span data-id="element-1919">
                                Week {req.weekNumber}
                                {req.endWeekNumber && req.endWeekNumber !== req.weekNumber ? ` – ${req.endWeekNumber}` : ''}
                              </span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3" data-id="element-1920">
                            {req.status === 'pending' && <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-id="element-1921">
                                <ClockIcon className="w-3 h-3" data-id="element-1922" /> Needs WW
                                Approval
                              </span>}
                            {req.status === 'brand-accepted' && <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-id="element-1923">
                                <ClockIcon className="w-3 h-3" data-id="element-1924" /> Awaiting WW
                                Approval
                              </span>}
                            {(req.status === 'accepted' || req.status === 'accepted-with-conditions') && <span className="inline-flex items-center gap-1 text-xs font-medium text-available bg-available/10 px-2 py-0.5 rounded-full" data-id="element-1925">
                                <CheckCircleIcon className="w-3 h-3" data-id="element-1926" /> Approved
                              </span>}
                            {req.status === 'rejected' && <span className="inline-flex items-center gap-1 text-xs font-medium text-urgent bg-urgent/10 px-2 py-0.5 rounded-full" data-id="element-1927">
                                <XCircleIcon className="w-3 h-3" data-id="element-1928" /> Declined
                              </span>}
                          </div>
                        </div>)}
                    </div>}
                  <div className="px-5 py-2 border-t border-border bg-surface-warm" data-id="element-1929">
                    <p className="text-[11px] text-text-tertiary" data-id="element-1930">
                      Woolworths will review and approve brand requests.
                      Grapevine has read-only visibility.
                    </p>
                  </div>
                </div>

                {/* Sent Opportunities History */}
                <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-1931">
                  <div className="px-5 py-3 border-b border-border bg-surface flex items-center gap-2" data-id="element-1932">
                    <FileTextIcon className="w-4 h-4 text-text-secondary" data-id="element-1933" />
                    <h3 className="text-sm font-semibold text-text-primary" data-id="element-1934">
                      Recent Opportunities
                    </h3>
                  </div>
                  {sentOpportunities.length === 0 ? <div className="px-5 py-8 text-center" data-id="element-1935">
                      <SendIcon className="w-8 h-8 text-text-tertiary mx-auto mb-2 opacity-40" data-id="element-1936" />
                      <p className="text-sm text-text-secondary" data-id="element-1937">
                        No opportunities sent yet
                      </p>
                      <p className="text-xs text-text-tertiary mt-1" data-id="element-1938">
                        Send your first media quote above
                      </p>
                    </div> : <div className="divide-y divide-border" data-id="element-1939">
                      {sentOpportunities.map(opp => <div key={opp.id} className="px-5 py-3 flex items-center justify-between" data-id="element-1940">
                          <div className="min-w-0" data-id="element-1941">
                            <div className="text-sm font-medium text-text-primary truncate" data-id="element-1942">
                              {opp.brandName}
                            </div>
                            <div className="text-xs text-text-tertiary mt-0.5 flex items-center gap-2" data-id="element-1943">
                              <span data-id="element-1944">
                                {opp.stores.length} store
                                {opp.stores.length !== 1 ? 's' : ''}
                              </span>
                              <span data-id="element-1945">·</span>
                              <span data-id="element-1946">
                                {new Date(opp.createdAt).toLocaleDateString('en-ZA', {
                          day: 'numeric',
                          month: 'short'
                        })}
                              </span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3" data-id="element-1947">
                            {opp.status === 'pending' && <span className="inline-flex items-center gap-1 text-xs font-medium text-requested bg-requested/10 px-2 py-0.5 rounded-full" data-id="element-1948">
                                <ClockIcon className="w-3 h-3" data-id="element-1949" /> Pending
                              </span>}
                            {opp.status === 'brand-accepted' && <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-id="element-1950">
                                <ClockIcon className="w-3 h-3" data-id="element-1951" /> Awaiting WW
                                Approval
                              </span>}
                            {(opp.status === 'accepted' || opp.status === 'accepted-with-conditions') && <span className="inline-flex items-center gap-1 text-xs font-medium text-available bg-available/10 px-2 py-0.5 rounded-full" data-id="element-1952">
                                <CheckCircleIcon className="w-3 h-3" data-id="element-1953" /> Accepted
                              </span>}
                            {opp.status === 'rejected' && <span className="inline-flex items-center gap-1 text-xs font-medium text-urgent bg-urgent/10 px-2 py-0.5 rounded-full" data-id="element-1954">
                                <XCircleIcon className="w-3 h-3" data-id="element-1955" /> Declined
                              </span>}
                          </div>
                        </div>)}
                    </div>}
                </div>
              </div>
            </div>
          </main>}

        <AddStoreModal isOpen={isAddStoreModalOpen} onClose={() => setIsAddStoreModalOpen(false)} onAddStore={onAddStore} data-id="element-1956" />

        <BookingOpportunityModal isOpen={isOpportunityModalOpen} stores={stores} brands={brands || []} onClose={() => setIsOpportunityModalOpen(false)} onSend={onSendOpportunity} data-id="element-1957" />
      </PageTransition>
    </div>;
}